import {DependencyInjectId} from '@@/package/const/DependencyInjectId';
import {type ApplicationContextInterface} from '@@/package/application/applicationContext';
import {PageModel} from '~~/package/mapper/common/model/PageModel';
import type ProSkillAssureApi from '~~/package/apis/ProSkillAssure/ProSkillAssureApi';
import {ProSkillAssureTrackModel} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureTrackModel';
import {
    ProSkillAssureAssessmentModel
} from '~~/package/mapper/proSkillAssure/model/assessment/ProSkillAssureAssessmentModel';

import {ProSkillAssureMyAssessmentModel} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureMyAssessmentModel';
import {
    ProSkillAssureAssessmentDetailModel
} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureAssessmentDetailModel';
import {
    ProSkillAssureAssessmentInfoModel
} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureAssessmentInfoModel';
import {
    ProSkillAssureAssessmentMyInfoModel
} from '~~/package/mapper/proSkillAssure/model/assessment/myInfo/ProSkillAssureAssessmentMyInfoModel';
import {ProSkillAssureHelper} from '~~/package/mapper/proSkillAssure/ProSkillAssureHelper';
import type ProSkillAssureEnvironmentReportRequest
    from '~~/package/apis/ProSkillAssure/request/ProSkillAssureEnvironmentReportRequest';
import {TaskModel} from '~~/package/mapper/learning/model/TaskModel';
import type {
    ProSkillAssureAnswerCodeModel
} from '~~/package/mapper/proSkillAssure/model/assessment/ProSkillAssureAnswerCodeModel';
import type ProSkillAssureAssessmentSetResponse
    from '~~/package/apis/ProSkillAssure/response/ProSkillAssureAssessmentSetResponse';
import {
    ProSkillAssureAssessmentDataModel
} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureAssessmentDataModel';
import type ProSkillAssureCreateRequest from '~~/package/apis/ProSkillAssure/request/ProSkillAssureCreateRequest';
import type ProSkillAssureAssessmentDetailResponse
    from '~~/package/apis/ProSkillAssure/response/ProSkillAssureAssessmentDetailResponse';
import {LevelAssessmentResultModel} from '~~/package/mapper/levelAssessment/model/LevelAssessmentResultModel';
import type ProSkillAssureTaskResponse from '~~/package/apis/ProSkillAssure/response/ProSkillAssureTaskResponse';
import {CautionType} from '~~/package/types/CautionType';
import type ProSkillAssureTimeZoneResponse
    from '~~/package/apis/ProSkillAssure/response/ProSkillAssureTimeZoneResponse';
import type CustomResponse from '~~/package/types/ResponseType';
import type {LearningTokenData} from '~~/package/mapper/learning/model/LearningTokenData';
import {
    ProSkillAssureTemplateCreateModel
} from '~~/package/mapper/proSkillAssure/model/template/ProSkillAssureTemplateCreateModel';
import {ProSkillAssureTemplateModel} from '~~/package/mapper/proSkillAssure/model/template/ProSkillAssureTemplateModel';
import {
    ProSkillAssureSampleAssessmentModel
} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureSampleAssessmentModel';
import type ProSkillAssureCandidateValidateResponse
    from '~~/package/apis/ProSkillAssure/response/ProSkillAssureCandidateValidateResponse';
import type ProSkillAssureAiRecommendEntryResponse
    from '~~/package/apis/ProSkillAssure/response/ProSkillAssureAiRecommendEntryResponse';
import type ProSkillAssureAiRecommendResponse
    from '~~/package/apis/ProSkillAssure/response/ProSkillAssureAiRecommendResponse';
import type ProSkillAssureAiRecommendRequest
    from '~~/package/apis/ProSkillAssure/request/ProSkillAssureAiRecommendRequest';
import {ProSkillAssureAiRecommendUsageModel} from '~~/package/mapper/proSkillAssure/model/aiRecommend/ProSkillAssureAiRecommendUsageModel';
import {ProSkillAssureAiRecommendResultModel} from '~~/package/mapper/proSkillAssure/model/aiRecommend/ProSkillAssureAiRecommendResultModel';
import {AiRecommendErrorType} from '~~/package/mapper/proSkillAssure/model/aiRecommend/AiRecommendErrorType';
import type {AxiosError} from 'axios';
import {ProSkillAssureTrackLevelModel} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureTrackLevelModel';
import {ProSkillAssureTestTemplateModel} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureTestTemplateModel';
import {ProSkillAssureAssessmentSetInfoModel} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureAssessmentSetInfoModel';
import {AdvancedAssessmentType} from '~~/package/mapper/proSkillAssure/model/AdvancedAssessmentType';

export const mapAiRecommendEntryResponse = (response: ProSkillAssureAiRecommendEntryResponse): ProSkillAssureAiRecommendEntryResponse => ({
    jobRoles: response.jobRoles ?? [],
    careerLevels: response.careerLevels ?? [],
    usage: ProSkillAssureAiRecommendUsageModel.create(response.usage ?? {})
});

export const mapAiRecommendResponse = (response: ProSkillAssureAiRecommendResponse): ProSkillAssureAiRecommendResponse => ({
    usage: ProSkillAssureAiRecommendUsageModel.create(response.usage ?? {}),
    results: (response.results ?? []).map(result => ProSkillAssureAiRecommendResultModel.create(result))
});

export const classifyAiRecommendError = (error: AxiosError): AiRecommendErrorType => {
    if (error.response?.status === 402) {
        const code = (error.response.data as { data?: string })?.data;
        if (code === 'CP_1016') return AiRecommendErrorType.QUOTA_EXCEEDED;
    }
    // 400 은 «입력 검증 실패» 와 «구독 없음» 두 가지이고 둘은 message 문구로만 갈린다.
    // 설계(PR #300 §오류 응답)는 화면이 이를 구분하지 않는다고 못박았다 — 입력 검증은 화면에서
    // 먼저 막고, 구독 없음은 평가 생성 진입에서 이미 막히므로 여기로 오지 않는다.
    return AiRecommendErrorType.LOGIC_FAILURE;
};

export const toTrackLevelModel = (
    result: ProSkillAssureAiRecommendResultModel,
    referenceUrlExposureFlag = false
): ProSkillAssureTrackLevelModel =>
    ProSkillAssureTrackLevelModel.create({
        trackLevelId: result.trackLevelId,
        title: result.trackLevelTitle ?? "",
        testTemplateList: result.testTemplateId === null ? [] : [ProSkillAssureTestTemplateModel.create({ id: result.testTemplateId })],
        assessmentContentType: result.contentType ?? AdvancedAssessmentType.CORE,
        referenceUrl: result.referenceUrl ?? "",
        referenceUrlExposureFlag,
        matchedKeywords: result.matchedKeywords,
        assessmentSetInfoVO: ProSkillAssureAssessmentSetInfoModel.create({
            trackTitle: result.trackName,
            trackLevelTitle: result.trackLevelTitle ?? "",
            taskCount: result.taskCount ?? 0,
            recommendTime: result.recommendTime ?? 0
        })
    });

export default class ProSkillAssureMapper {
    protected proSkillAssureApi: ProSkillAssureApi;

    constructor(context: ApplicationContextInterface) {
        this.proSkillAssureApi = context.getService<ProSkillAssureApi>(DependencyInjectId.ProSkillAssureApi)
    }

    public async getProSkillAssessmentTrack(page: number, itemSize: number, categoryList?: string[], field?: string, type?: string[], trackTitle?: string): Promise<PageModel<ProSkillAssureTrackModel>> {
        const response: CustomResponse<PageModel<ProSkillAssureTrackModel>> = (await this.proSkillAssureApi.getProSkillAssessmentTrack(page, itemSize, categoryList, field, type, trackTitle)).data;
        const pageData = PageModel.create(response.data);
        pageData.data = pageData.data.map(track => ProSkillAssureHelper.mappingProSkillAssureAssessmentWith(track))
        return pageData
    }

    public async getProSkillAssureAssessmentList(page: number, itemSize: number, filters?: string[], assessmentTitle?: string): Promise<PageModel<ProSkillAssureAssessmentDataModel>> {
        const response: CustomResponse<PageModel<ProSkillAssureAssessmentDataModel>> = (await this.proSkillAssureApi.getProSkillAssureAssessmentList(page, itemSize, filters, assessmentTitle)).data;
        const pageData = PageModel.create(response.data);
        pageData.data = pageData.data.map(assessment => ProSkillAssureAssessmentDataModel.create(assessment));
        return pageData;
    }

    public async getMyAssessmentList(page?: number, size?: number, status?: string[], isAdmin?: boolean): Promise<PageModel<ProSkillAssureMyAssessmentModel>> {
        const response: CustomResponse<PageModel<ProSkillAssureMyAssessmentModel>> = (await this.proSkillAssureApi.getMyAssessmentList(page, size, status, isAdmin)).data;
        const pageData = PageModel.create(response.data);
        pageData.data = pageData.data.map(assessment => ProSkillAssureMyAssessmentModel.create(assessment))
        return pageData
    }

    public async getSampleAssessmentList(lang: string, page: number, size: number): Promise<PageModel<ProSkillAssureSampleAssessmentModel>> {
        const response: CustomResponse<PageModel<ProSkillAssureSampleAssessmentModel>> = (await this.proSkillAssureApi.getSampleAssessmentList(lang, page, size)).data;
        const pageData = PageModel.create(response.data);
        pageData.data = pageData.data.map(assessment => ProSkillAssureSampleAssessmentModel.create(assessment))
        return pageData
    }

    public async getProSkillAssureAssessmentInfo(customAssessmentId: number): Promise<ProSkillAssureAssessmentInfoModel> {
        const response: CustomResponse<ProSkillAssureAssessmentInfoModel> = (await this.proSkillAssureApi.getProSkillAssureAssessmentInfo(customAssessmentId)).data;
        return response.data;
    }

    public async getProSkillAssureAssessmentDetail(customAssessmentId: number): Promise<ProSkillAssureAssessmentDetailModel> {
        const response: CustomResponse<ProSkillAssureAssessmentDetailResponse> = (await this.proSkillAssureApi.getProSkillAssureAssessmentDetail(customAssessmentId)).data;
        return ProSkillAssureHelper.mappingProSkillAssureAssessmentDetail(response.data);
    }

    public async deleteProSkillAssureCandidates(customAssessmentId: number, userIds: number[]): Promise<boolean> {
        const response: CustomResponse<boolean> = (await this.proSkillAssureApi.deleteProSkillAssureCandidates(customAssessmentId, userIds)).data;
        return response.data;
    }

    public async setProSkillAssureAssessment(assessmentSets: ProSkillAssureCreateRequest): Promise<number> {
        return (await this.proSkillAssureApi.setProSkillAssureAssessment(assessmentSets)).data.data;
    }

    public async addProSkillAssureCandidateToAssessment(emailList: string[], assessmentId: number): Promise<boolean> {
        return (await this.proSkillAssureApi.addProSkillAssureCandidateToAssessment(emailList, assessmentId)).data.data;
    }

    public async validateProSkillAssureCandidates(assessmentId: number, emailList: string[]): Promise<ProSkillAssureCandidateValidateResponse> {
        const response: CustomResponse<ProSkillAssureCandidateValidateResponse> = (await this.proSkillAssureApi.validateProSkillAssureCandidates(assessmentId, emailList)).data;
        return response.data;
    }

    public async updateProSkillAssureAssessment(customAssessmentId: number, assessment: ProSkillAssureCreateRequest): Promise<boolean> {
        return (await this.proSkillAssureApi.updateProSkillAssureAssessment(customAssessmentId, assessment)).data.data;
    }

    public async deleteProSkillAssureAssessment(customAssessmentId: number): Promise<boolean> {
        const response: CustomResponse<boolean> = (await this.proSkillAssureApi.deleteProSkillAssureAssessment(customAssessmentId)).data;
        return response.data;
    }

    public async getProSkillAssureAssessment(assessmentId: number): Promise<ProSkillAssureAssessmentModel> {
        const response: CustomResponse<ProSkillAssureAssessmentSetResponse> = (await this.proSkillAssureApi.getProSkillAssureAssessmentData(assessmentId)).data;
        return ProSkillAssureHelper.mappingProSkillAssureAssessment(response.data);
    }

    public async getAssessmentRemainingTime(assessmentId: number): Promise<number> {
        const response: CustomResponse<number> = (await this.proSkillAssureApi.getAssessmentRemainingTime(assessmentId)).data;
        return response.data;
    }

    public async getProSkillAssureAssessmentTask(assessmentId: number, testTemplateId: number, taskId: number): Promise<TaskModel> {
        const response: CustomResponse<ProSkillAssureTaskResponse> = (await this.proSkillAssureApi.getProSkillAssureAssessmentTask(assessmentId, testTemplateId, taskId)).data;
        return ProSkillAssureHelper.mappingTask(response.data);
    }

    public async getProSkillAssureEmbedUrl(assessmentId: number, testTemplateId: number, taskId: number): Promise<string> {
        const response: CustomResponse<string> = (await this.proSkillAssureApi.getProSkillAssureEmbedUrl(assessmentId, testTemplateId, taskId)).data;
        return response.data ?? '';
    }

    public async requestAnswer(assessmentId: number, testTemplateId: number, taskId: number, answer: ProSkillAssureAnswerCodeModel, tokenData: LearningTokenData): Promise<boolean> {
        const response: CustomResponse<boolean> = (await this.proSkillAssureApi.requestAnswer(assessmentId, testTemplateId, taskId, answer, tokenData)).data;
        return response.data;
    }

    public async updateRulesAgreeFlag(assessmentId: number, rulesAgreeFlag: boolean): Promise<boolean> {
        const response: CustomResponse<boolean> = (await this.proSkillAssureApi.updateRulesAgreeFlag(assessmentId, rulesAgreeFlag)).data;
        return response.data;
    }

    /** TSK-11215: START returns the newly issued seat id; COMPLETED returns nothing useful. */
    public async updateStatus(assessmentId: number, status: "COMPLETED" | "START"): Promise<string | null> {
        const response: CustomResponse<string> = (await this.proSkillAssureApi.updateStatus(assessmentId, status)).data;
        return response?.data ?? null;
    }

    public async getProSkillAssureMyInfo(assessmentId: number): Promise<ProSkillAssureAssessmentMyInfoModel> {
        const response: CustomResponse<ProSkillAssureAssessmentMyInfoModel> = (await this.proSkillAssureApi.getProSkillAssureMyInfo(assessmentId)).data;
        return ProSkillAssureAssessmentMyInfoModel.create(response.data);
    }

    public async getProSkillAssureAssessmentSampleList(testTemplateId: number): Promise<LevelAssessmentResultModel[]> {
        const response: CustomResponse<LevelAssessmentResultModel[]> = (await this.proSkillAssureApi.getProSkillAssureAssessmentSampleList(testTemplateId)).data;
        return ProSkillAssureHelper.mappingProSkillAssureAssessmentSampleList(response.data);
    }

    public async addProSkillAssureAssessmentSaveCandidateAction(customAssessmentId: number, suspiciousAction: CautionType, actionContent?: string): Promise<number> {
        const response: CustomResponse<number> = (await this.proSkillAssureApi.addProSkillAssureAssessmentSaveCandidateAction(customAssessmentId, suspiciousAction, actionContent)).data;
        return response.data;
    }

    public async getTimezoneList(): Promise<ProSkillAssureTimeZoneResponse[]> {
        const response: CustomResponse<ProSkillAssureTimeZoneResponse[]> = (await this.proSkillAssureApi.getTimezoneList()).data;
        return response.data;
    }

    public async getProSkillAssureTemplate(page: number, itemSize: number, lang: string, title?: string): Promise<PageModel<ProSkillAssureTemplateModel>> {
        const response: CustomResponse<PageModel<ProSkillAssureTemplateModel>> = (await this.proSkillAssureApi.getProSkillAssureTemplate(page, itemSize, lang, title)).data;
        const pageData = PageModel.create(response.data);
        pageData.data = pageData.data.map(template => ProSkillAssureTemplateModel.create(template));
        return pageData;
    }

    public async addProSkillAssureTemplate(request: ProSkillAssureTemplateCreateModel): Promise<boolean> {
        const response: CustomResponse<boolean> = (await this.proSkillAssureApi.addProSkillAssureTemplate(request)).data;
        return response.data;
    }

    public async modifyProSkillAssureTemplate(title: string, assessmentTemplateId: number): Promise<boolean> {
        const response: CustomResponse<boolean> = (await this.proSkillAssureApi.modifyProSkillAssureTemplate(title, assessmentTemplateId)).data;
        return response.data;
    }

    public async removeProSkillAssureTemplate(assessmentTemplateId: number): Promise<boolean> {
        const response: CustomResponse<boolean> = (await this.proSkillAssureApi.removeProSkillAssureTemplate(assessmentTemplateId)).data;
        return response.data;
    }

    /** ASMT-PROCTOR-16 (TSK-11692): 운영팀 알림용. 응답 본문이 없고 실패해도 응시 흐름을 막지 않는다. */
    public async reportEnvironmentEvent(customAssessmentId: number, request: ProSkillAssureEnvironmentReportRequest): Promise<void> {
        await this.proSkillAssureApi.reportEnvironmentEvent(customAssessmentId, request);
    }

    public async getAiRecommendEntry(): Promise<ProSkillAssureAiRecommendEntryResponse> {
        const response: CustomResponse<ProSkillAssureAiRecommendEntryResponse> = (await this.proSkillAssureApi.getAiRecommendEntry()).data;
        return mapAiRecommendEntryResponse(response.data);
    }

    public async requestAiRecommend(request: ProSkillAssureAiRecommendRequest): Promise<ProSkillAssureAiRecommendResponse> {
        const response: CustomResponse<ProSkillAssureAiRecommendResponse> = (await this.proSkillAssureApi.requestAiRecommend(request)).data;
        return mapAiRecommendResponse(response.data);
    }
}
